/**
 * 378 (#1266, trial): LiveAgent's chat widget. The account and the chat button come from the runtime `config.js`
 * (Constitution II); without both nothing is loaded and no request reaches LiveAgent. The script is added once and the
 * button created once; the logged-in customer's details are handed over with LiveAgent's own JavaScript API
 * (`setUserDetails`, `clearAllUserDetails`).
 */
export interface LiveAgentConfig {
  /** The account, e.g. `https://carii.ladesk.com` (no trailing slash). */
  url: string;
  buttonId: string;
}

interface LiveAgentApi {
  createButton(buttonId: string, element: unknown): { onClick?: () => void } | unknown;
  setUserDetails(email: string, firstName: string, lastName: string, phone: string): void;
  clearAllUserDetails(): void;
}

declare global {
  interface Window {
    LiveAgent?: LiveAgentApi;
  }
}

// LiveAgent's own integration snippet gives its script this id; kept identical in case track.js looks for it.
export const SCRIPT_ID = 'la_x2s6df8d';

/** The account and button from config, or null when either is missing or not a plain https address and id. */
export function liveAgentConfig(raw: {
  liveAgentUrl?: string;
  liveAgentButtonId?: string;
}): LiveAgentConfig | null {
  const url = (raw.liveAgentUrl ?? '').trim().replace(/\/+$/, '');
  const buttonId = (raw.liveAgentButtonId ?? '').trim();
  // A placeholder left by envsubst ("${LIVEAGENT_URL}") or an empty value means "not configured".
  if (!/^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(url)) return null;
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(buttonId)) return null;
  return { url, buttonId };
}

/**
 * Add LiveAgent's script once; when it has loaded, create the chat button once and call `onReady`. A second call (a
 * re-render, a remount) adds nothing.
 */
let requested = false;
// 403 (#1317): the chat button LiveAgent created, so "contact us" can open the chat (its own snippets call onClick).
let button: { onClick?: () => void } | null = null;

/** Tests only: forget that the script was requested. */
export function resetLiveAgentForTests(): void {
  requested = false;
  button = null;
}

/** 403: open LiveAgent's chat, as a click on its button would. */
export function openLiveAgent(): void {
  button?.onClick?.();
}

export function loadLiveAgent(
  cfg: LiveAgentConfig,
  onReady: () => void,
  doc: Document = document,
): void {
  // LiveAgent removes its own script tag once loaded, so "once" is remembered here, not read from the page.
  if (requested || doc.getElementById(SCRIPT_ID)) return;
  requested = true;
  const s = doc.createElement('script');
  s.id = SCRIPT_ID;
  s.defer = true;
  s.src = `${cfg.url}/scripts/track.js`;
  s.onload = () => {
    const api = (doc.defaultView ?? window).LiveAgent;
    if (!api) return;
    button = api.createButton(cfg.buttonId, s) as { onClick?: () => void } | null;
    onReady();
  };
  doc.body.appendChild(s);
}

/** Who LiveAgent should see: the customer's details, or nobody (logged out, or staff impersonating). */
export function applyCustomer(
  customer: { email: string; first_name?: string; last_name?: string; phone?: string } | null,
  api: LiveAgentApi | undefined = typeof window === 'undefined' ? undefined : window.LiveAgent,
): void {
  if (!api) return;
  api.clearAllUserDetails();
  if (customer) {
    api.setUserDetails(
      customer.email,
      customer.first_name ?? '',
      customer.last_name ?? '',
      customer.phone ?? '',
    );
  }
}
